import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeftRightIcon,
  BookOpenIcon,
  CalendarIcon,
  ChevronDownIcon,
  ClockIcon,
  ExternalLinkIcon,
  FilePenLineIcon,
  FilesIcon,
  HelpCircleIcon,
  SparklesIcon,
  InboxIcon,
  KeyRoundIcon,
  LogOutIcon,
  MapPinIcon,
  MenuIcon,
  MessageCircleIcon,
  MessageSquareTextIcon,
  MicIcon,
  MonitorIcon,
  NewspaperIcon,
  ReceiptIcon,
  SettingsIcon,
  ShieldAlertIcon,
  ShieldCheckIcon,
  UsersIcon,
  UsersRoundIcon,
  XIcon,
} from 'lucide-react';
import {
  type FeatureKey,
  type ListNotificationsResponse,
  type TenantModule,
  type MeldingVoorkeuren,
  type Notification,
  type UserRole,
  MELDING_VOORKEUREN_STANDAARD,
  TENANT_ADMIN_ROLES,
  tenantHasModule,
} from '@kennisbank/shared';
import { useAuth } from '../auth/AuthProvider.js';
import { apiGet, apiPost } from '../api/client.js';
import { nieuweMeldingen, tabTitel, telbaar } from '../lib/meldingen.js';
import { MeldingStrook } from './MeldingStrook.js';
import { NotificatieBel } from './NotificatieBel.js';
import { ProfileBanner } from './ProfileBanner.js';
import { WelcomePanel } from './WelcomePanel.js';
import { navItemVisible } from './nav-visibility.js';
import { useTenantBrand } from '../lib/tenant-brand.js';

/**
 * App shell — sidebar (logo + gegroepeerde nav + vertrouwensmodel) + topbar.
 * Welke items een gebruiker ziet volgt uit de modules van zijn tenant
 * (TENANT_MODULES, ADR 0018), zijn rol en de server-features.
 */
interface NavItem {
  to: string;
  label: string;
  description: string;
  icon: typeof MapPinIcon;
  allowed?: UserRole[];
  /**
   * Server-afgeleide zichtbaarheid (issue #38): staat de feature in
   * `user.features` op false, dan verdwijnt het item — geen dood menu-item
   * meer dat op een 404-melding uitloopt. Zonder feature-koppeling (of zonder
   * features-object van een oudere backend) altijd tonen.
   */
  feature?: FeatureKey;
  /**
   * Toont een ongelezen-teller. Vandaag leefde die indicatie alleen ín
   * Berichten.tsx, waardoor een DM onzichtbaar was tot je die pagina opende.
   * Voor een melding met een klok van 72 uur is dat te mager.
   */
  badge?: BadgeKey;
  /** Alleen voor kantoorpersoneel (isHeadOffice) en suppohrt_admin. */
  headOffice?: boolean;
  /** Verruimt `headOffice` met gemarkeerde teamleiders (isTeamleider). */
  teamleider?: boolean;
  /** Alleen voor e-mailadressen op de verwijsbrief-allowlist (ADR 0017). */
  verwijsbrief?: boolean;
  /** Submenu-groep binnen Beheer; alleen voor items in de Beheer-groep. */
  beheerGroep?: BeheerGroep;
  /** Alleen voor wie op de allowlist voor demo-toegangscodes staat. */
  demoToegang?: boolean;
  /** Verborgen als deze module voor de tenant uit staat (TENANT_MODULES). */
  module?: TenantModule;
  /** Toon het item toch als de module uit staat; de route toont dan "(nog) niet beschikbaar". */
  nietBeschikbaar?: boolean;
}

type BadgeKey = 'berichten' | 'incidenten' | 'declaraties';

// Beheer is één sidebar-item; de onderdelen staan als submenu ín de pagina
// (besluit Jos 3 sep 2026, prototype-branch proto/beheer-sidebar). De groepen
// volgen wat een beheerder ermee dóet, niet welke module het is.
type BeheerGroep = 'inhoud' | 'beoordelen' | 'organisatie';
const BEHEER_GROEPEN: { key: BeheerGroep; label: string }[] = [
  { key: 'inhoud', label: 'Inhoud & modules' },
  { key: 'beoordelen', label: 'Te beoordelen' },
  { key: 'organisatie', label: 'Organisatie' },
];
const beheerLabel = (label: string): string => label.replace(/^Beheer /, '');

interface NavGroup {
  label: string;
  items: NavItem[];
}

const SUPPOHRT: UserRole[] = ['suppohrt_admin', 'suppohrt_medewerker'];

// Elke pagina rendert zijn eigen <Layout>, dus de sidebar remount bij elke
// navigatie en de nav-scrollpositie zou terugspringen naar 0 (het "springerige"
// menu). Module-scope onthoudt de laatste scrollTop over remounts binnen de
// SPA-sessie. ponytail: module-var i.p.v. een persistente layout-route — de
// upgrade is Layout in de router hijsen als er meer redenen komen om te delen.
let savedNavScroll = 0;

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Actueel',
    items: [
      {
        to: '/nieuws',
        label: 'Nieuws',
        description: 'Berichten en updates',
        icon: NewspaperIcon,
        module: 'nieuws',
      },
      {
        to: '/agenda',
        label: 'Agenda',
        description: 'Aankomende evenementen',
        icon: CalendarIcon,
        module: 'agenda',
      },
    ],
  },
  {
    label: "Collega's",
    items: [
      {
        to: '/smoelenboek',
        label: 'Smoelenboek POH-GGZ',
        description: 'Overzicht medewerkers',
        icon: UsersIcon,
        module: 'smoelenboek',
      },
      {
        to: '/berichten',
        label: 'Chats',
        description: 'Kanalen en directe berichten',
        icon: MessageCircleIcon,
        badge: 'berichten',
        module: 'chats',
      },
      {
        to: '/intervisie',
        label: 'Intervisie',
        description: 'Intervisie via Teams',
        icon: UsersRoundIcon,
        allowed: SUPPOHRT,
        module: 'intervisie',
        nietBeschikbaar: true,
      },
    ],
  },
  {
    label: 'Ondersteuning',
    items: [
      {
        to: '/sara',
        label: 'Sara',
        description: 'Spraakgerichte rapportage',
        icon: MicIcon,
        // Geen module-koppeling: staat Sara uit voor de tenant, dan blijft het
        // item staan en toont de pagina "nog niet beschikbaar" (ADR 0018).
        feature: 'sara',
      },
      {
        to: '/kennisagent',
        label: 'Mila',
        description: 'Stel een zorginhoudelijke vraag',
        icon: MessageSquareTextIcon,
        module: 'mila',
      },
      {
        to: '/documenten',
        label: 'Documenten',
        description: 'Bladeren door de kennisbank',
        icon: FilesIcon,
        feature: 'documenten',
        module: 'documenten',
      },
      {
        to: '/verwijsbrieven',
        label: 'Verwijsbrieven',
        description: 'Concepten, 72 uur bewaard',
        icon: FilePenLineIcon,
        verwijsbrief: true,
        module: 'verwijsbrieven',
      },
      {
        to: '/e-learnings',
        label: 'Academy',
        description: 'Online leermodules (LMS)',
        icon: BookOpenIcon,
        allowed: SUPPOHRT,
        feature: 'elearnings',
        module: 'academy',
        nietBeschikbaar: true,
      },
    ],
  },
  {
    label: 'Zorgverleners',
    items: [
      {
        to: '/sociale-kaart',
        label: 'Sociale Kaart',
        description: 'Zorgverleners zoeken',
        icon: MapPinIcon,
        module: 'socialeKaart',
      },
      {
        to: '/e-health',
        label: 'E-health integratie',
        description: 'Online programma, vragenlijst, beeldbellen',
        icon: MonitorIcon,
        allowed: SUPPOHRT,
        module: 'eHealth',
      },
    ],
  },
  {
    label: 'Formulieren & Links',
    items: [
      {
        to: '/uren',
        label: 'Uren',
        description: 'Uren schrijven naar TimeChimp',
        icon: ClockIcon,
        allowed: SUPPOHRT,
        feature: 'uren',
        module: 'uren',
      },
      {
        to: '/declaraties',
        label: 'Declaraties',
        description: 'Kosten declareren',
        icon: ReceiptIcon,
        feature: 'declaraties',
        module: 'declaraties',
      },
      {
        to: '/incident-melden',
        label: 'Incident melden',
        description: 'Datalek of beveiligingsincident',
        icon: ShieldAlertIcon,
        feature: 'incidentMelden',
        module: 'incidenten',
      },
      {
        to: '/handige-links',
        label: 'Handige links',
        description: 'Formulieren en integraties',
        icon: ExternalLinkIcon,
        allowed: SUPPOHRT,
        feature: 'handigeLinks',
        module: 'handigeLinks',
      },
    ],
  },
  {
    label: 'Informatie Kennishub',
    items: [
      {
        to: '/wat-is-nieuw',
        label: 'Wat is nieuw',
        description: 'Recente wijzigingen in de app',
        icon: SparklesIcon,
        module: 'watIsNieuw',
      },
      {
        to: '/help',
        label: 'Help',
        description: 'Welkomstpaneel & instructies',
        icon: HelpCircleIcon,
      },
    ],
  },
  {
    label: 'Beheer',
    items: [
      {
        to: '/beheer/sociale-kaart',
        beheerGroep: 'inhoud',
        label: 'Beheer Sociale Kaart',
        description: 'Admin paneel',
        icon: SettingsIcon,
        allowed: ['suppohrt_admin'],
      },
      {
        to: '/beheer/gebruikers',
        beheerGroep: 'organisatie',
        label: 'Beheer Gebruikers',
        description: 'Mila & Sara per gebruiker',
        icon: UsersIcon,
        // Beheerders van de eigen tenant; de backend leest alleen die tenant.
        allowed: [...TENANT_ADMIN_ROLES],
      },
      {
        to: '/beheer/kennisbank',
        beheerGroep: 'inhoud',
        label: 'Beheer Kennisbank',
        description: 'Documenten beheren',
        icon: SettingsIcon,
        allowed: ['suppohrt_admin'],
      },
      {
        to: '/beheer/welkomstpaneel',
        beheerGroep: 'inhoud',
        label: 'Beheer Welkomstpaneel',
        description: 'Welkomsttekst & disclaimer',
        icon: SettingsIcon,
        // Een RHOGO-beheerder bewerkt alleen het paneel van de eigen tenant.
        allowed: [...TENANT_ADMIN_ROLES],
      },
      {
        to: '/beheer/wat-is-nieuw',
        beheerGroep: 'inhoud',
        label: 'Beheer Wat is nieuw',
        description: 'Changelog schrijven & vrijgeven',
        icon: SparklesIcon,
        allowed: ['suppohrt_admin'],
      },
      {
        to: '/beheer/wijzigingsverzoeken',
        beheerGroep: 'beoordelen',
        label: 'Wijzigingsverzoeken',
        description: 'Feedback & meldingen',
        icon: InboxIcon,
        allowed: ['suppohrt_admin'],
      },
      {
        to: '/beheer/intervisie',
        beheerGroep: 'inhoud',
        label: 'Beheer Intervisie',
        description: 'Groepen en leden',
        icon: UsersRoundIcon,
        allowed: ['suppohrt_admin'],
        module: 'intervisie',
      },
      {
        to: '/beheer/e-learning',
        beheerGroep: 'inhoud',
        label: 'Beheer E-learnings',
        description: 'Cursussen & voortgang',
        icon: BookOpenIcon,
        allowed: ['suppohrt_admin'],
        module: 'academy',
      },
      {
        to: '/beheer/incidenten',
        beheerGroep: 'beoordelen',
        label: 'Beheer Incidenten',
        description: 'Meldingen en termijnen',
        icon: ShieldAlertIcon,
        allowed: ['suppohrt_admin'],
        badge: 'incidenten',
        module: 'incidenten',
      },
      {
        to: '/beheer/declaraties',
        beheerGroep: 'beoordelen',
        label: 'Beheer Declaraties',
        description: 'Beoordelen en versturen',
        icon: ReceiptIcon,
        allowed: SUPPOHRT,
        headOffice: true,
        teamleider: true,
        feature: 'declaraties',
        badge: 'declaraties',
        module: 'declaraties',
      },
      {
        to: '/beheer/tijdregistratie',
        beheerGroep: 'organisatie',
        label: 'Beheer TimeChimp',
        description: 'Koppeling & weekoverzicht',
        icon: ClockIcon,
        // Kantoor doet de urenadministratie (besluit Jos 3 sep 2026), dus
        // hetzelfde vangnetpatroon als Beheer Declaraties.
        allowed: SUPPOHRT,
        headOffice: true,
        module: 'uren',
      },
      {
        to: '/beheer/demo-toegang',
        beheerGroep: 'organisatie',
        label: 'Demo-toegang',
        description: 'Toegangscodes voor prospects',
        icon: KeyRoundIcon,
        allowed: SUPPOHRT,
        demoToegang: true,
      },
      {
        to: '/beheer/audit-log',
        beheerGroep: 'organisatie',
        label: 'Audit log',
        description: 'Wie deed wat wanneer',
        icon: ShieldCheckIcon,
        allowed: ['suppohrt_admin'],
      },
    ],
  },
];

function tenantLabel(tenantId: string): string {
  if (tenantId === 'suppohrt') return 'SUPPOHRT';
  return tenantId.toUpperCase();
}

function roleLabel(role: UserRole, tenantId: string): string {
  const rhogo = tenantId === 'rhogo';
  switch (role) {
    case 'suppohrt_admin':
      return 'SUPPOHRT Admin';
    case 'suppohrt_medewerker':
      return 'Medewerker';
    case 'zorggroep_admin':
      return rhogo ? 'RHOGO Beheerder' : 'Zorggroep Admin';
    case 'zorggroep_gebruiker':
      return rhogo ? 'RHOGO Medewerker' : 'Zorggroep Gebruiker';
  }
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return '?';
  if (parts.length === 1) return (parts[0][0] ?? '?').toUpperCase();
  return ((parts[0][0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase();
}

/**
 * App-schil-state die de navigatie moet overleven. Elke pagina rendert zijn
 * eigen <Layout>, dus alles wat hier vroeger in Layout stond (profiel, badges,
 * aanwezigheid, meldingen + polling) werd bij ÉLKE klik opnieuw opgehaald:
 * acht requests per navigatie, en een actieve beheerder zat daarmee binnen
 * minuten aan de rate limiter (Linda, 17 sep 2026). De provider staat in
 * App.tsx om de router heen, dus dit laadt één keer per sessie en pollt op
 * één interval, ongeacht hoeveel pagina's er voorbijkomen.
 */
interface OnlineUser {
  id: string;
  displayName: string;
}

interface Shell {
  onlineIds: Set<string>;
  online: OnlineUser[];
  profileCompleted: boolean | null;
  profilePhotoUrl: string | undefined;
  weergavenaam: string | undefined;
  badges: Record<BadgeKey, number>;
  notifications: Notification[];
  unreadNotifications: number;
  strook: Notification[];
  loadNotifications: () => void;
  openStrook: (melding: Notification) => void;
  sluitStrook: (id: string) => void;
}

const ShellContext = createContext<Shell>({
  onlineIds: new Set(),
  online: [],
  profileCompleted: null,
  profilePhotoUrl: undefined,
  weergavenaam: undefined,
  badges: { berichten: 0, incidenten: 0, declaraties: 0 },
  notifications: [],
  unreadNotifications: 0,
  strook: [],
  loadNotifications: () => undefined,
  openStrook: () => undefined,
  sluitStrook: () => undefined,
});

export function useOnlineUserIds(): Set<string> {
  return useContext(ShellContext).onlineIds;
}

export function ShellProvider({ children }: { children: ReactNode }): JSX.Element {
  const { user } = useAuth();
  const { appNaam } = useTenantBrand();
  const location = useLocation();
  const navigate = useNavigate();
  const [online, setOnline] = useState<OnlineUser[]>([]);
  const onlineIds = useMemo(() => new Set(online.map((u) => u.id)), [online]);
  const [profileCompleted, setProfileCompleted] = useState<boolean | null>(null);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | undefined>(undefined);
  const [weergavenaam, setWeergavenaam] = useState<string | undefined>(undefined);
  const [voorkeuren, setVoorkeuren] = useState<MeldingVoorkeuren>(MELDING_VOORKEUREN_STANDAARD);

  // Load self-profile (completion + signed photo URL + meldingsvoorkeuren).
  // Drives the aanvul-banner, the header avatar and the strook. Re-runs on
  // 'profile-saved' (fired by the profielpagina bij opslaan) so a fresh save
  // reflects direct. Voor élke rol: externen hebben ook een bel; de banner
  // zelf blijft in Layout achter isInternal.
  const uid = user?.id;
  useEffect(() => {
    if (!uid) return;
    const load = (): void => {
      void apiGet<{
        user: {
          profileCompletedAt?: string;
          photoUrl?: string;
          weergavenaam?: string;
          meldingen?: MeldingVoorkeuren;
        };
      }>('/api/v1/profile/me')
        .then((d) => {
          setProfileCompleted(!!d.user.profileCompletedAt);
          setProfilePhotoUrl(d.user.photoUrl);
          setWeergavenaam(d.user.weergavenaam);
          setVoorkeuren(d.user.meldingen ?? MELDING_VOORKEUREN_STANDAARD);
        })
        .catch(() => undefined);
    };
    load();
    window.addEventListener('profile-saved', load);
    return () => window.removeEventListener('profile-saved', load);
  }, [uid]);

  // Ongelezen-tellers voor de sidebar. Berichten telt de eigen unreadCounts,
  // Incidenten de meldingen die nog op triage wachten. Interval van 60s en niet
  // de 5s van de berichtenpagina zelf: dit draait op élke pagina, en een badge
  // die een minuut oud is verandert niets aan een termijn van 72 uur.
  const [badges, setBadges] = useState<Record<BadgeKey, number>>({
    berichten: 0,
    incidenten: 0,
    declaraties: 0,
  });
  // Aanwezigheid: rijdt mee op het bestaande 60s-interval hieronder en gaat
  // via ShellContext naar de pagina's die stippen en filters tonen.
  const isAdmin = user?.role === 'suppohrt_admin';
  const isKantoor = isAdmin || user?.isHeadOffice === true;
  const isTeamleider = user?.isTeamleider === true;
  const myUserId = user?.id;
  // Alleen pollen wat de tenant aan heeft: een uitgezette module geeft 403 met
  // een access.refused-regel per minuut, en die regels voeden de signaal-janitor.
  const tenantId = user?.tenantId;
  const heeftSmoelenboek = tenantHasModule(tenantId, 'smoelenboek');
  const heeftChats = tenantHasModule(tenantId, 'chats');
  const heeftIncidenten = tenantHasModule(tenantId, 'incidenten');
  const heeftDeclaraties = tenantHasModule(tenantId, 'declaraties');

  // Meldingen voor de bel in de header. Los van de badges hierboven: die zijn
  // intern-only, de bel is er voor élke rol. Faalt stil zolang
  // NOTIFICATIES_ENABLED uit staat (404) — dan blijft de bel simpelweg leeg.
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  // Strook: wat er sinds de vorige poll bijkwam. Refs en geen state voor de
  // vergelijking, zodat de poll-callback niet bij elke wijziging opnieuw
  // gebonden hoeft te worden.
  const [strook, setStrook] = useState<Notification[]>([]);
  const vorigeRef = useRef<Notification[] | null>(null);
  const voorkeurenRef = useRef(voorkeuren);
  voorkeurenRef.current = voorkeuren;
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const loadNotifications = useCallback((): void => {
    void apiGet<ListNotificationsResponse>('/api/v1/notifications')
      .then((d) => {
        const nieuw = nieuweMeldingen(
          vorigeRef.current,
          d.notifications,
          voorkeurenRef.current,
          pathRef.current,
        );
        vorigeRef.current = d.notifications;
        setNotifications(d.notifications);
        setUnreadNotifications(d.unread);
        if (nieuw.length > 0) {
          // Hooguit drie tegelijk; de rest staat in de bel.
          setStrook((s) =>
            [...s.filter((x) => !nieuw.some((n) => n.id === x.id)), ...nieuw].slice(-3),
          );
        }
      })
      .catch(() => undefined);
  }, []);

  const sluitStrook = useCallback((id: string): void => {
    setStrook((s) => s.filter((x) => x.id !== id));
  }, []);
  const openStrook = (melding: Notification): void => {
    sluitStrook(melding.id);
    void apiPost(`/api/v1/notifications/${melding.id}/read`)
      .catch(() => undefined)
      .finally(() => loadNotifications());
    navigate(melding.link);
  };

  // Tabtitel-teller, altijd aan; volgt dezelfde schakelaars als de strook.
  useEffect(() => {
    document.title = tabTitel(telbaar(notifications, voorkeuren).length, appNaam);
    return () => {
      document.title = tabTitel(0);
    };
  }, [notifications, voorkeuren, appNaam]);

  // Eigen interval voor de meldingen: 20s en óók in een verborgen tabblad,
  // anders blijft de tabtitel achterlopen tot je terugklikt. Deze route
  // stempelt geen aanwezigheid (zie authenticate), dus dit houdt niemand
  // vals "online". Budget: 3/min hier plus maximaal ~12/min op de
  // Berichten-pagina blijft onder de 20/min van de rate limiter.
  useEffect(() => {
    if (!myUserId) return;
    vorigeRef.current = null;
    loadNotifications();
    const t = setInterval(loadNotifications, 20_000);
    return () => clearInterval(t);
  }, [myUserId, loadNotifications]);

  useEffect(() => {
    if (!myUserId) return;
    const load = (): void => {
      if (heeftSmoelenboek) {
        void apiGet<{ users: OnlineUser[] }>('/api/v1/users/presence')
          // ?? []: tijdens een deploy kan een oude revisie nog het oude
          // antwoord geven, en ShellProvider staat boven de router — een
          // throw hier is een wit scherm, geen kapotte chip.
          .then((d) => setOnline(d.users ?? []))
          .catch(() => undefined);
      }
      if (heeftChats) {
        void apiGet<{ conversations: { unreadCounts: Record<string, number> }[] }>(
          '/api/v1/dm/conversations',
        )
          .then((d) => {
            const n = d.conversations.reduce((sum, c) => sum + (c.unreadCounts[myUserId] ?? 0), 0);
            setBadges((b) => ({ ...b, berichten: n }));
          })
          .catch(() => undefined);
      }
      // Faalt stil zolang INCIDENT_MELDING_ENABLED uit staat (404). Een
      // ontbrekende badge mag nooit de hele sidebar laten struikelen.
      if (isAdmin && heeftIncidenten) {
        void apiGet<{ incidents: unknown[] }>('/api/v1/beheer/incidents?status=nieuw')
          .then((d) => setBadges((b) => ({ ...b, incidenten: d.incidents.length })))
          .catch(() => undefined);
      }
      // Alleen kantoor/admin/teamleider pollt de declaratiewachtrij: een gewone
      // medewerker zou hier een 403 mét access.refused-regel per minuut
      // schrijven, en die regels voeden de incident-signaal-janitor.
      if ((isKantoor || isTeamleider) && heeftDeclaraties) {
        void apiGet<{ declaraties: unknown[] }>('/api/v1/beheer/declaraties?status=ingediend')
          .then((d) => setBadges((b) => ({ ...b, declaraties: d.declaraties.length })))
          .catch(() => undefined);
      }
    };
    load();
    // Elk request houdt de eigen lastActiveAt vers, dus deze poll is het
    // aanwezigheidssignaal. Een verborgen tabblad telt overdag mee (Diederik en
    // Mandy, 20 okt 2026: wie naar een ander tabblad ging stond offline). Laptop
    // dicht of browser dicht = geen timers meer = na 5 min offline. Buiten
    // 07:00-18:00 alleen in een zichtbaar tabblad, zodat een open vergeten tab
    // op een aan gelaten computer niet de hele nacht "online" toont.
    // ponytail: lokale klok van de browser, NL-gebruikers; geen idle-timer.
    const tick = (): void => {
      const uur = new Date().getHours();
      if (document.visibilityState === 'visible' || (uur >= 7 && uur < 18)) load();
    };
    const t = setInterval(tick, 60_000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [
    heeftSmoelenboek,
    heeftChats,
    heeftIncidenten,
    heeftDeclaraties,
    isAdmin,
    isKantoor,
    isTeamleider,
    myUserId,
  ]);

  return (
    <ShellContext.Provider
      value={{
        onlineIds,
        online,
        profileCompleted,
        profilePhotoUrl,
        weergavenaam,
        badges,
        notifications,
        unreadNotifications,
        strook,
        loadNotifications,
        openStrook,
        sluitStrook,
      }}
    >
      {children}
    </ShellContext.Provider>
  );
}

/** Groene stip rechtsonder een avatar: deze collega is nu online. */
export function OnlineDot({ large = false }: { large?: boolean }): JSX.Element {
  return (
    <span
      title="Nu online"
      aria-label="Nu online"
      className={`absolute bottom-0.5 right-0.5 rounded-full bg-emerald-500 ring-2 ring-white ${
        large ? 'h-4 w-4' : 'h-3.5 w-3.5'
      }`}
    />
  );
}

export function Layout({ children }: { children: ReactNode }): JSX.Element {
  const { user, signOut } = useAuth();
  const brand = useTenantBrand();
  const location = useLocation();
  const navigate = useNavigate();
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const {
    online,
    profileCompleted,
    profilePhotoUrl,
    weergavenaam,
    badges,
    notifications,
    unreadNotifications,
    strook,
    loadNotifications,
    openStrook,
    sluitStrook,
  } = useContext(ShellContext);
  const isInternal = user?.role === 'suppohrt_admin' || user?.role === 'suppohrt_medewerker';
  // Online-chip en profiel horen bij het Smoelenboek, dus bij de tenant, niet bij de rol.
  const heeftSmoelenboek = tenantHasModule(user?.tenantId, 'smoelenboek');
  const myUserId = user?.id;

  // Zelfgekozen weergavenaam wint van de Entra-naam, net als in het Smoelenboek.
  const headerName = weergavenaam?.trim() || user?.displayName || '';

  // Jezelf niet meetellen: je eigen poll stempelt je lastActiveAt, dus je bent
  // per definitie online en de teller zou "1 online" tonen als je in je eentje
  // werkt.
  const collegas = online.filter((u) => u.id !== myUserId);
  const collegasOnline = collegas.length;

  const showBanner = isInternal && !bannerDismissed && profileCompleted === false;

  if (!user) return <></>;

  // Rol én server-features bepalen samen wat zichtbaar is (issue #38).
  const itemVisible = (i: NavItem): boolean =>
    navItemVisible(
      i,
      user.role,
      user.features,
      user.isHeadOffice === true,
      user.isTeamleider === true,
      user.demoToegangBeheer === true,
      user.verwijsbrief === true,
      user.tenantId,
    );

  const allItems = NAV_GROUPS.flatMap((g) => g.items).filter(itemVisible);
  // Header-titel: sidebar-item op de huidige route, anders de titel van een
  // off-sidebar route (profiel/help). NIET terugvallen op allItems[0] — dat
  // toonde 'Nieuws' op /profile.
  const OFFNAV_TITLES: Record<string, string> = {
    '/profile': 'Mijn profiel',
    '/help': 'Help',
  };
  const matchedNav = allItems.find((i) => location.pathname.startsWith(i.to));
  const headerTitle = matchedNav?.label ?? OFFNAV_TITLES[location.pathname] ?? 'Kennishub';
  const isZorggroep = user.role.startsWith('zorggroep');
  const [menuOpen, setMenuOpen] = useState(false);
  const inRhogo = user.tenantWissel?.actief === true;
  const [wisselBezig, setWisselBezig] = useState(false);
  const [wisselFout, setWisselFout] = useState(false);
  const wisselNaar = async (naar: 'rhogo' | 'thuis'): Promise<void> => {
    setMenuOpen(false);
    setWisselBezig(true);
    setWisselFout(false);
    try {
      await apiPost('/api/v1/tenant-wissel', { naar });
      // Volledige herlaad i.p.v. caches verversen: schil, auth en elke pagina
      // houden eigen state vast, en geen enkel stuk SUPPOHRT-data mag na de
      // wissel nog op het scherm staan (of andersom).
      window.location.assign('/');
    } catch (err) {
      console.error('Tenant-wissel mislukt', err);
      setWisselBezig(false);
      setWisselFout(true);
    }
  };
  const [navOpen, setNavOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const navScrollRef = useRef<HTMLElement | null>(null);

  const inBeheer = location.pathname.startsWith('/beheer/');
  const beheerItems = NAV_GROUPS.find((g) => g.label === 'Beheer')?.items.filter(itemVisible) ?? [];
  const beheerBadgeTotal = beheerItems.reduce((n, i) => n + (i.badge ? badges[i.badge] : 0), 0);
  // Landingspagina van het item "Beheer": het eerste onderdeel met een
  // openstaande teller, anders het eerste zichtbare onderdeel. Zo kom je na
  // een klik op "Beheer 3" ook echt uit waar die 3 wacht.
  const beheerHome = (beheerItems.find((i) => i.badge && badges[i.badge] > 0) ?? beheerItems[0])
    ?.to;
  const badgeText = (n: number): string => (n > 99 ? '99+' : String(n));

  // Herstel de bewaarde scrollpositie vóór paint zodat het menu niet zichtbaar
  // naar boven springt bij het openen van een lager menu-item.
  useLayoutEffect(() => {
    if (navScrollRef.current) navScrollRef.current.scrollTop = savedNavScroll;
  }, []);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const navItemClass = (isActive: boolean): string =>
    [
      'group relative flex items-start gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors',
      isActive
        ? 'bg-brand-50 text-ink-900 font-semibold'
        : 'text-ink-900/70 hover:bg-brand-50/60 hover:text-ink-900',
    ].join(' ');

  const renderItem = (item: NavItem): JSX.Element => {
    const Icon = item.icon;
    return (
      <NavLink key={item.to} to={item.to} className={({ isActive }) => navItemClass(isActive)}>
        {({ isActive }) => (
          <>
            {isActive && (
              <span
                aria-hidden="true"
                className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full bg-streep"
              />
            )}
            <Icon
              size={17}
              strokeWidth={2}
              className={`mt-0.5 shrink-0 ${isActive ? 'text-brand-700' : 'text-ink-900/55'}`}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate">{item.label}</span>
                {!!item.badge && badges[item.badge] > 0 && (
                  <span
                    className="ml-auto shrink-0 rounded-full bg-brand-500 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white"
                    aria-label={`${badges[item.badge]} ongelezen`}
                  >
                    {badges[item.badge] > 99 ? '99+' : badges[item.badge]}
                  </span>
                )}
              </div>
              <div
                className={`truncate text-[11px] font-normal ${
                  isActive ? 'text-ink-900/60' : 'text-ink-900/45'
                }`}
              >
                {item.description}
              </div>
            </div>
          </>
        )}
      </NavLink>
    );
  };

  return (
    <div className="flex h-dvh bg-[#f7fafa] text-ink-900">
      {navOpen && (
        <button
          type="button"
          aria-label="Sluit navigatie"
          onClick={() => setNavOpen(false)}
          className="fixed inset-0 z-30 bg-ink-900/40 lg:hidden"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-64 shrink-0 flex-col border-r border-brand-100 bg-white transition-transform duration-200 lg:static lg:translate-x-0 ${
          navOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="flex items-center justify-between border-b border-brand-50 px-3 py-2 lg:hidden">
          <span className="text-xs font-semibold uppercase tracking-wide text-ink-700/70">
            Menu
          </span>
          <button
            type="button"
            onClick={() => setNavOpen(false)}
            aria-label="Sluit navigatie"
            className="rounded-md p-1.5 text-ink-900/60 hover:bg-brand-50 hover:text-ink-900"
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className="border-b border-brand-50 px-5 py-5">
          <img src={brand.logo} alt={brand.logoAlt} className={brand.logoClass} />
          <div className="mt-2 text-[11px] font-medium uppercase tracking-wider text-ink-700/70">
            Kennishub
          </div>
          {/* Een tenant met eigen huisstijl staat al met naam en logo bovenaan. */}
          {isZorggroep && !brand.eigen && (
            <div className="mt-3 flex items-center gap-2 border-t border-brand-50 pt-3">
              <div className="text-[10px] font-medium uppercase tracking-wider text-ink-800/50">
                In licentie voor
              </div>
              <div className="rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[11px] font-bold text-ink-800">
                {tenantLabel(user.tenantId)}
              </div>
            </div>
          )}
        </div>

        <nav
          ref={navScrollRef}
          onScroll={(e) => {
            savedNavScroll = e.currentTarget.scrollTop;
          }}
          aria-label="Hoofdmenu"
          className="thin-scroll flex-1 overflow-y-auto px-3 py-4"
        >
          {NAV_GROUPS.map((group) => {
            const visibleItems = group.items.filter(itemVisible);
            if (visibleItems.length === 0) return null;
            if (group.label === 'Beheer' && beheerHome) {
              return (
                <div key={group.label} className="mb-3">
                  <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-brand-700/70">
                    Beheer
                  </div>
                  {/* Link i.p.v. NavLink: actief op élke /beheer-route, niet alleen op beheerHome. */}
                  <Link
                    to={beheerHome}
                    aria-current={inBeheer ? 'page' : undefined}
                    className={navItemClass(inBeheer)}
                  >
                    {inBeheer && (
                      <span
                        aria-hidden="true"
                        className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full bg-streep"
                      />
                    )}
                    <SettingsIcon
                      size={17}
                      strokeWidth={2}
                      className={`mt-0.5 shrink-0 ${inBeheer ? 'text-brand-700' : 'text-ink-900/55'}`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate">Beheer</span>
                        {beheerBadgeTotal > 0 && (
                          <span
                            className="ml-auto shrink-0 rounded-full bg-brand-500 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white"
                            aria-label={`${beheerBadgeTotal} te beoordelen`}
                          >
                            {badgeText(beheerBadgeTotal)}
                          </span>
                        )}
                      </div>
                      <div className="truncate text-[11px] font-normal text-ink-900/45">
                        Inhoud, beoordelen en organisatie
                      </div>
                    </div>
                  </Link>
                </div>
              );
            }

            return (
              <div key={group.label} className="mb-3">
                <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-brand-700/70">
                  {group.label}
                </div>
                <div className="space-y-0.5">{visibleItems.map((item) => renderItem(item))}</div>
              </div>
            );
          })}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {(inRhogo || wisselFout) && (
          <div
            role="status"
            className="flex shrink-0 flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-petrol-500 px-4 py-1.5 text-[13px] text-white"
          >
            {inRhogo && (
              <>
                <span className="font-semibold">Je werkt nu in RHOGO</span>
                <button
                  type="button"
                  onClick={() => void wisselNaar('thuis')}
                  disabled={wisselBezig}
                  className="rounded border border-white/60 px-2 py-0.5 text-[12px] font-semibold hover:bg-white/15 disabled:opacity-60"
                >
                  {wisselBezig ? 'Bezig met wisselen…' : 'Terug naar SUPPOHRT'}
                </button>
              </>
            )}
            {wisselFout && <span>Wisselen lukte niet. Probeer het opnieuw.</span>}
          </div>
        )}
        <header className="flex h-16 shrink-0 items-center gap-3 border-b border-brand-100 bg-white px-4 sm:px-6">
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            aria-label="Open navigatie"
            className="-ml-1 rounded-md p-2 text-ink-900/70 hover:bg-brand-50 hover:text-ink-900 lg:hidden"
          >
            <MenuIcon size={20} />
          </button>
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-brand-600">
              Kennishub
            </div>
            <div className="-mt-0.5 truncate font-display text-[15px] font-semibold text-ink-900">
              {headerTitle}
            </div>
          </div>

          <div className="ml-auto flex items-center gap-1 sm:gap-3">
            {heeftSmoelenboek && (
              // group + focus-within i.p.v. hover-state in JS: het paneel moet
              // ook met het toetsenbord bereikbaar zijn, en dat is precies wat
              // CSS hier gratis geeft.
              <div className="group relative hidden sm:block">
                <NavLink
                  to="/smoelenboek?online=1"
                  title="Collega's die nu online zijn"
                  className="flex items-center gap-1.5 rounded-full border border-brand-100 px-2.5 py-1 text-[12px] font-medium text-ink-800/70 transition-colors hover:bg-brand-50"
                >
                  {/* Grijze stip bij 0: de teller blijft staan zodat "niemand
                      online" ook een antwoord is, en niet lijkt op een chip die
                      nog moet laden. */}
                  <span
                    aria-hidden="true"
                    className={`h-2 w-2 rounded-full ${
                      collegasOnline > 0 ? 'bg-emerald-500' : 'bg-ink-900/25'
                    }`}
                  />
                  {collegasOnline === 0 ? 'Alleen jij online' : `${collegasOnline} online`}
                </NavLink>

                {collegasOnline > 0 && (
                  <div className="invisible absolute right-0 top-full z-30 w-56 pt-1 opacity-0 transition-opacity group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                    <div className="max-h-80 overflow-y-auto rounded-lg border border-brand-100 bg-white py-1 shadow-lg">
                      {collegas.map((c) => (
                        <NavLink
                          key={c.id}
                          to={`/smoelenboek?persoon=${c.id}`}
                          className="flex items-center gap-2 px-3 py-1.5 text-[13px] text-ink-800/80 hover:bg-brand-50"
                        >
                          <span
                            aria-hidden="true"
                            className="h-2 w-2 shrink-0 rounded-full bg-emerald-500"
                          />
                          <span className="truncate">{c.displayName}</span>
                        </NavLink>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <NotificatieBel
              notifications={notifications}
              unread={unreadNotifications}
              onRefresh={loadNotifications}
            />
            <MeldingStrook items={strook} onOpen={openStrook} onSluit={sluitStrook} />
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                className="flex items-center gap-2.5 rounded-lg px-2 py-1 text-left transition-colors hover:bg-brand-50"
                title="Gebruikersmenu"
              >
                <div
                  className={`flex h-9 w-9 items-center justify-center overflow-hidden rounded-full text-sm font-semibold text-white ${
                    isZorggroep ? 'bg-amber-700' : 'bg-brand-500'
                  }`}
                >
                  {profilePhotoUrl ? (
                    <img src={profilePhotoUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    initials(headerName)
                  )}
                </div>
                <div className="hidden leading-tight sm:block">
                  <div className="text-[13px] font-semibold text-ink-800">{headerName}</div>
                  <div className="flex items-center gap-1.5 text-[11px] text-ink-800/60">
                    <span
                      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-white ${
                        isZorggroep ? 'bg-amber-700' : 'bg-ink-800'
                      }`}
                    >
                      {tenantLabel(user.tenantId)}
                    </span>
                    <span>{roleLabel(user.role, user.tenantId)}</span>
                  </div>
                </div>
                <ChevronDownIcon
                  size={14}
                  className={`text-ink-900/40 transition-transform ${menuOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {menuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-full z-40 mt-2 w-60 overflow-hidden rounded-lg border border-brand-100 bg-white shadow-lg"
                >
                  <div className="border-b border-brand-50 px-4 py-3 sm:hidden">
                    <div className="text-[13px] font-semibold text-ink-800">{headerName}</div>
                    <div className="text-[11px] text-ink-800/60">
                      {roleLabel(user.role, user.tenantId)}
                    </div>
                  </div>
                  {heeftSmoelenboek && (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setMenuOpen(false);
                        navigate('/profile');
                      }}
                      className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-ink-900 hover:bg-brand-50"
                    >
                      <SettingsIcon size={15} />
                      <span>Profiel bewerken</span>
                    </button>
                  )}
                  {user.tenantWissel?.mag && (
                    <button
                      type="button"
                      role="menuitem"
                      disabled={wisselBezig}
                      onClick={() => void wisselNaar(inRhogo ? 'thuis' : 'rhogo')}
                      className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-ink-900 hover:bg-brand-50 disabled:opacity-60"
                    >
                      <ArrowLeftRightIcon size={15} />
                      <span>{inRhogo ? 'Terug naar SUPPOHRT' : 'Wissel naar RHOGO'}</span>
                    </button>
                  )}
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      void signOut();
                    }}
                    className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-ink-900 hover:bg-brand-50"
                  >
                    <LogOutIcon size={15} />
                    <span>Uitloggen</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {showBanner && <ProfileBanner onDismiss={() => setBannerDismissed(true)} />}
        {/* App-breed: de start is Nieuws, niet Mila, dus de modal moet overal
            kunnen vuren. Niet op /help, daar staat de tekst al voluit. */}
        {location.pathname !== '/help' && <WelcomePanel variant="modal-only" />}
        <main className="min-h-0 flex-1 overflow-y-auto">
          {inBeheer && beheerItems.length > 0 ? (
            // Submenu van Beheer: kolom links op breed scherm, strook bovenaan op smal.
            <div className="flex min-h-full flex-col md:flex-row">
              <nav
                aria-label="Beheer"
                className="shrink-0 border-b border-brand-100 bg-white/60 md:w-52 md:border-b-0 md:border-r"
              >
                {/* Rand en achtergrond op de nav (rekt mee), alleen de inhoud plakt. */}
                <div className="px-3 py-3 md:sticky md:top-0 md:py-6">
                  {BEHEER_GROEPEN.map((bg) => {
                    const items = beheerItems.filter((i) => i.beheerGroep === bg.key);
                    if (items.length === 0) return null;
                    return (
                      <div key={bg.key} className="mb-2 md:mb-4">
                        <div className="px-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-brand-700/70">
                          {bg.label}
                        </div>
                        {/* Smal scherm: items van een groep op één regel; breed: onder elkaar. */}
                        <div className="flex flex-wrap gap-1 md:block">
                          {items.map((item) => (
                            <NavLink
                              key={item.to}
                              to={item.to}
                              className={({ isActive }) =>
                                `flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] ${
                                  isActive
                                    ? 'bg-brand-50 font-semibold text-ink-900'
                                    : 'text-ink-900/75 hover:bg-brand-50/60'
                                }`
                              }
                            >
                              <span className="truncate">{beheerLabel(item.label)}</span>
                              {!!item.badge && badges[item.badge] > 0 && (
                                <span
                                  className="ml-auto rounded-full bg-brand-500 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white"
                                  aria-label={`${badges[item.badge]} ongelezen`}
                                >
                                  {badgeText(badges[item.badge])}
                                </span>
                              )}
                            </NavLink>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </nav>
              <div className="min-w-0 flex-1">{children}</div>
            </div>
          ) : (
            children
          )}
        </main>
      </div>
    </div>
  );
}
